import {
  DocumentNode,
  LazyQueryHookOptions,
  OperationVariables,
  TypedDocumentNode,
  useLazyQuery,
} from '@apollo/client';
import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_POLL_TIMEOUT_MS = 60000;
const DEFAULT_POLL_INTERVAL_MS = 5000;

export interface QueryLongPollHookOptions<TData, TVariables extends OperationVariables>
  extends Omit<LazyQueryHookOptions<TData, TVariables>, 'pollInterval'> {
  /**
   * The maximum time to poll
   * @default 60 seconds
   */
  pollTimeout?: number;
  /**
   * The interval between poll requests
   * @default 5 seconds
   */
  pollInterval?: number;
}

export interface QueryLongPollReturn<TData, TVariables extends OperationVariables = OperationVariables> {
  startLongPoll: (variables?: TVariables) => void;
  endLongPoll: () => void;
  isPolling: boolean;
  data: TData | undefined;
}

export const useQueryLongPoll = <
  TData = Record<string, unknown>,
  TVariables extends OperationVariables = OperationVariables,
>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options: QueryLongPollHookOptions<TData, TVariables>,
): QueryLongPollReturn<TData, TVariables> => {
  const pollInterval = options?.pollInterval ?? DEFAULT_POLL_INTERVAL_MS;
  const pollTimeout = options?.pollTimeout ?? DEFAULT_POLL_TIMEOUT_MS;

  const [isPolling, setIsPolling] = useState(false);
  const [data, setData] = useState<TData | undefined>(undefined);

  // Use refs to track polling state and variables to avoid stale closures
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentVariablesRef = useRef<TVariables | undefined>(undefined);

  // Don't pass pollInterval to Apollo - we'll handle polling manually
  const { pollInterval: _unusedPollInterval, pollTimeout: _unusedPollTimeout, ...apolloOptions } = options;

  const [lazyQuery] = useLazyQuery<TData, TVariables>(query, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
    ...apolloOptions,
    onCompleted: (queryData) => {
      setData(queryData);
      apolloOptions.onCompleted?.(queryData);
    },
  });

  const clearPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, []);

  const endLongPoll = useCallback(() => {
    clearPolling();
    setIsPolling(false);
  }, [clearPolling]);

  // Manual polling implementation that always uses fresh variables
  const startLongPoll = useCallback(
    (variables?: TVariables) => {
      // Stop any existing polling first
      clearPolling();

      // Store the variables to use for polling
      currentVariablesRef.current = variables;
      setIsPolling(true);

      // Execute initial query
      lazyQuery(variables ? { variables } : undefined);

      // Set up polling interval
      pollIntervalRef.current = setInterval(() => {
        lazyQuery(currentVariablesRef.current ? { variables: currentVariablesRef.current } : undefined);
      }, pollInterval);

      // Set up timeout to stop polling
      pollTimeoutRef.current = setTimeout(() => {
        endLongPoll();
      }, pollTimeout);
    },
    [lazyQuery, pollInterval, pollTimeout, clearPolling, endLongPoll],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearPolling();
    };
  }, [clearPolling]);

  return {
    startLongPoll,
    endLongPoll,
    isPolling,
    data,
  };
};
